import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

interface MathTextProps {
  children: string;
  className?: string;
  block?: boolean;
}

/** Remove redundant nested \( \) that cause parser errors */
function flattenNestedMath(text: string): string {
  let result = '';
  let depth = 0;
  let i = 0;
  while (i < text.length) {
    if (text[i] === '\\' && text[i+1] === '(') {
      depth++;
      if (depth === 1) result += '\\(';
      i += 2;
    } else if (text[i] === '\\' && text[i+1] === ')') {
      depth--;
      if (depth === 0) result += '\\)';
      if (depth < 0) depth = 0;
      i += 2;
    } else {
      result += text[i];
      i++;
    }
  }
  return result;
}

/** Convert Unicode math chars to LaTeX inside captured expressions */
function unicodeToLatex(expr: string): string {
  let r = expr;
  // Superscripts (multi-char first)
  r = r.replace(/⁻¹/g, '^{-1}');
  r = r.replace(/²/g, '^{2}');
  r = r.replace(/³/g, '^{3}');
  r = r.replace(/⁴/g, '^{4}');
  r = r.replace(/⁵/g, '^{5}');
  r = r.replace(/⁶/g, '^{6}');
  r = r.replace(/⁸/g, '^{8}');
  r = r.replace(/⁹/g, '^{9}');
  r = r.replace(/⁰/g, '^{0}');
  r = r.replace(/ⁿ/g, '^{n}');
  r = r.replace(/ˣ/g, '^{x}');
  r = r.replace(/ʸ/g, '^{y}');
  // Subscripts
  r = r.replace(/₁/g, '_{1}');
  r = r.replace(/₂/g, '_{2}');
  r = r.replace(/₃/g, '_{3}');
  r = r.replace(/ₙ/g, '_{n}');
  // Roots
  r = r.replace(/(\d?)√(\d+)/g, (_, c, n) => c ? `${c}\\sqrt{${n}}` : `\\sqrt{${n}}`);
  r = r.replace(/∛(\d+)/g, '\\sqrt[3]{$1}');
  // Symbols
  r = r.replace(/−/g, '-');
  r = r.replace(/×/g, '\\times ');
  r = r.replace(/÷/g, '\\div ');
  r = r.replace(/π/g, '\\pi ');
  r = r.replace(/≤/g, '\\leq ');
  r = r.replace(/≥/g, '\\geq ');
  r = r.replace(/±/g, '\\pm ');
  r = r.replace(/°/g, '^{\\circ}');
  return r;
}

/** Convert Unicode math patterns to LaTeX-delimited expressions in plain text */
function convertUnicodeMath(text: string): string {
  let r = text;

  // --- Fractions (most specific first) ---

  // Parenthesized fractions: (expr)/(expr)
  r = r.replace(/\(([^)]+)\)\/\(([^)]+)\)/g, (_, num, den) =>
    `\\(\\frac{${unicodeToLatex(num)}}{${unicodeToLatex(den)}}\\)`
  );

  // digit(s)/(expr): 1/(√5 + √2), 2/(√5 − 1)
  r = r.replace(/(\d+)\/\(([^)]+)\)/g, (_, num, den) =>
    `\\(\\frac{${num}}{${unicodeToLatex(den)}}\\)`
  );

  // digit(s)/√digit(s): 6/√3
  r = r.replace(/(\d+)\/√(\d+)/g, (_, num, den) =>
    `\\(\\frac{${num}}{\\sqrt{${den}}}\\)`
  );

  // digit(s)/variable: 1/x, 2/n (not preceded/followed by letters)
  r = r.replace(/(?<![a-zA-Z\d\\])(\d+)\/([a-zA-Z])(?![a-zA-Z\d])/g, (_, num, den) =>
    `\\(\\frac{${num}}{${den}}\\)`
  );

  // variable/digit: x/3, a/2 (not preceded/followed by parens or letters)
  r = r.replace(/(?<![a-zA-Z\\])([a-zA-Z])\/(\d+)(?![.\d])/g, (_, num, den) =>
    `\\(\\frac{${num}}{${den}}\\)`
  );

  // (expr)/digit: (x+3)/2, (2x−1)/3
  r = r.replace(/\(([^)]+)\)\/(\d+)/g, (_, num, den) =>
    `\\(\\frac{${unicodeToLatex(num)}}{${den}}\\)`
  );

  // variable/variable: a/b (restricted to common math variable letters)
  r = r.replace(/(?<![a-zA-Z])([abcdfhknpqrstuvwxyz])\/([abcdfhknpqrstuvwxyz])(?![a-zA-Z])/g, (_, num, den) =>
    `\\(\\frac{${num}}{${den}}\\)`
  );

  // --- Square and cube roots ---

  // √(expr) with optional leading coefficient
  r = r.replace(/(\d?)√\(([^)]+)\)/g, (_, coeff, inner) =>
    coeff ? `\\(${coeff}\\sqrt{${unicodeToLatex(inner)}}\\)` : `\\(\\sqrt{${unicodeToLatex(inner)}}\\)`
  );

  // n√m: 2√2, 3√5
  r = r.replace(/(\d)√(\d+)/g, '\\($1\\sqrt{$2}\\)');

  // √m: √50, √18
  r = r.replace(/√(\d+)/g, '\\(\\sqrt{$1}\\)');

  // Cube root: ∛64
  r = r.replace(/∛(\d+)/g, '\\(\\sqrt[3]{$1}\\)');

  // --- Unicode superscript expressions ---

  // Parenthesized expression + superscript: (−2)³, (x − 2)², (0.2)³
  r = r.replace(/\(([^)]+)\)([²³⁴⁵⁶⁸⁹⁰⁻¹ⁿˣʸ]+)/g, (_, inner, sup) =>
    `\\(\\left(${unicodeToLatex(inner)}\\right)${unicodeToLatex(sup)}\\)`
  );

  // Single-letter variable with optional digit prefix + superscript: x², 2x³, n⁴
  // (avoid matching multi-letter like cm²)
  r = r.replace(/(?<![a-zA-Z])(\d?)([a-zA-Z])([²³⁴⁵⁶⁸⁹⁰⁻¹ⁿˣʸ]+)(?![a-zA-Z])/g, (_, d, v, sup) =>
    `\\(${d}${v}${unicodeToLatex(sup)}\\)`
  );

  // Standalone number + superscript: 2⁵, 10⁻³, 3⁴
  r = r.replace(/(?<![a-zA-Z\d.])(\d+)([²³⁴⁵⁶⁸⁹⁰⁻¹ⁿˣʸ]+)(?![a-zA-Z\d])/g, (_, base, sup) =>
    `\\(${base}${unicodeToLatex(sup)}\\)`
  );

  return r;
}

/**
 * Wrap remaining plain-text math expressions in LaTeX delimiters.
 * Catches patterns like "a = 14", "x = 3.5", "3x + 5 = 17".
 * Only runs on segments NOT already inside \(...\) delimiters.
 */
function convertPlainMathToLatex(text: string): string {
  let r = text;

  // Variable = number assignments: a = 14, x = 3.5, b = -2
  r = r.replace(
    /(?<![\\a-zA-Z])([a-zA-Z])\s*=\s*(-?\d+(?:\.\d+)?)(?![a-zA-Z\d\\])/g,
    (_, v, n) => `\\(${v} = ${n}\\)`
  );

  return r;
}

/**
 * Preprocess text: convert Unicode math symbols to LaTeX delimiters.
 * Only processes portions NOT already inside \(...\) or \[...\] delimiters.
 */
function preprocessMathSymbols(text: string): string {
  const segments: string[] = [];
  const delimiterRegex = /\$\$([\s\S]*?)\$\$|\$([^\n$]+?)\$|\\\((.*?)\\\)|\\\[(.*?)\]/g;
  let lastIndex = 0;
  let match;

  while ((match = delimiterRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      let seg = text.substring(lastIndex, match.index);
      seg = convertUnicodeMath(seg);
      seg = convertPlainMathToLatex(seg);
      segments.push(seg);
    }
    segments.push(match[0]);
    lastIndex = delimiterRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    let seg = text.substring(lastIndex);
    seg = convertUnicodeMath(seg);
    seg = convertPlainMathToLatex(seg);
    segments.push(seg);
  }

  return segments.join('');
}

export default function MathText({ children, className = '', block = false }: MathTextProps) {
  const containerRef = useRef<HTMLDivElement | HTMLSpanElement>(null);
  const hasTable = children.includes('[TABLE]');

  useEffect(() => {
    if (!containerRef.current) return;

    // Flatten nested Math delimiters first
    const flattened = flattenNestedMath(children);

    // Preprocess Unicode math symbols into LaTeX-delimited expressions
    const preprocessed = preprocessMathSymbols(flattened);

    // Extract [TABLE]...[/TABLE] blocks, replace with placeholders
    const tables: string[] = [];
    const text = preprocessed.replace(/\[TABLE\](.*?)\[\/TABLE\]/g, (_, content: string) => {
      const rows = content.split(';;');
      let tbl = '<table class="math-data-table"><thead><tr>';
      rows[0].split('|').forEach(h => { tbl += `<th>${escapeHtml(h.trim())}</th>`; });
      tbl += '</tr></thead><tbody>';
      for (let i = 1; i < rows.length; i++) {
        tbl += '<tr>';
        rows[i].split('|').forEach(c => { tbl += `<td>${escapeHtml(c.trim())}</td>`; });
        tbl += '</tr>';
      }
      tbl += '</tbody></table>';
      const placeholder = `\x00TBL${tables.length}\x00`;
      tables.push(tbl);
      return placeholder;
    });

    let html = '';
    const latexRegex = /\$\$([\s\S]*?)\$\$|\$([^\n$]+?)\$|\\\((.*?)\\\)|\\\[(.*?)\\\]|(\d+)\s+(\d+)\/(\d+)|(\d+)\/(\d+)/g;

    let lastIndex = 0;
    let m;

    while ((m = latexRegex.exec(text)) !== null) {
      html += escapeHtml(text.substring(lastIndex, m.index));

      try {
        if (m[1] !== undefined) {
          const rendered = katex.renderToString(m[1], {
            displayMode: true,
            throwOnError: false,
            output: 'html',
          });
          html += rendered;
        } else if (m[2] !== undefined) {
          const rendered = katex.renderToString(m[2], {
            displayMode: false,
            throwOnError: false,
            output: 'html',
          });
          html += `<span class="whitespace-nowrap">${rendered}</span>`;
        } else if (m[3] !== undefined) {
          const rendered = katex.renderToString(m[3], {
            displayMode: false,
            throwOnError: false,
            output: 'html',
          });
          html += `<span class="whitespace-nowrap">${rendered}</span>`;
        } else if (m[4] !== undefined) {
          const rendered = katex.renderToString(m[4], {
            displayMode: true,
            throwOnError: false,
            output: 'html',
          });
          html += rendered;
        } else if (m[5]) {
          const latex = `${m[5]}\\frac{${m[6]}}{${m[7]}}`;
          const rendered = katex.renderToString(latex, {
            displayMode: false,
            throwOnError: false,
            output: 'html',
          });
          html += `<span class="whitespace-nowrap">${rendered}</span>`;
        } else if (m[8]) {
          const latex = `\\frac{${m[8]}}{${m[9]}}`;
          const rendered = katex.renderToString(latex, {
            displayMode: false,
            throwOnError: false,
            output: 'html',
          });
          html += `<span class="whitespace-nowrap">${rendered}</span>`;
        }
      } catch (e) {
        html += escapeHtml(m[0]);
      }

      lastIndex = latexRegex.lastIndex;
    }

    html += escapeHtml(text.substring(lastIndex));

    // Restore table placeholders
    tables.forEach((tbl, i) => {
      html = html.replace(`\x00TBL${i}\x00`, tbl);
    });

    containerRef.current.innerHTML = html;
  }, [children, block]);

  const Component = (block || hasTable) ? 'div' : 'span';
  return <Component ref={containerRef as any} className={`math-serif${className ? ` ${className}` : ''}`} />;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
